import { genRetrieveMapboxUrl } from '@/hooks/useMapboxQuery';
import apiRequest from '@/services/apiRequest';
import { IMapboxRetrievedSuggestion } from '@/services/types/mapbox/mapboxApi';

// Module-scoped cache for retrieve results — survives re-renders, cleared on page reload
const retrieveCache = new Map<string, string>();

interface MapboxValue {
  mapbox_id: string;
  full_address?: string;
}

/**
 * Retrieves the full address from Mapbox API for a given mapbox_id if full_address is not already available
 * @param value - Object containing mapbox_id and optional full_address
 * @returns Promise<string> - The full address or empty string if retrieval fails
 */
export const retrieveMapboxFullAddress = async (value: MapboxValue): Promise<string> => {
  try {
    if (value?.full_address) {
      return value.full_address;
    }

    // Check cache before making API call
    const cached = retrieveCache.get(value.mapbox_id);
    if (cached) {
      return cached;
    }

    const mapboxUrl = genRetrieveMapboxUrl(value.mapbox_id);
    if (!mapboxUrl) {
      return '';
    }

    const retrievedSuggestion = await apiRequest<IMapboxRetrievedSuggestion>({
      url: mapboxUrl,
    });

    const fullAddress = retrievedSuggestion.features[0]?.properties.full_address || '';

    if (fullAddress) {
      retrieveCache.set(value.mapbox_id, fullAddress);
    }

    return fullAddress;
  } catch {
    return '';
  }
};
